import { useMemo } from "react";
import { useWalletConnect } from "@btc-vision/walletconnect";
import { getContract } from "opnet";
import { MOTOSETTLE_CONFIG } from "../config";
import {
  ESCROW_CORE_OPNET_ABI,
  ROUTER_OPNET_ABI,
  FEE_POLICY_OPNET_ABI,
  ARB_REGISTRY_OPNET_ABI,
  USER_REPUTATION_OPNET_ABI,
} from "../utils/opnetAbis";

export interface OpnetEscrowClient {
  escrowContract: any;
  routerContract: any;
  feePolicyContract: any;
  arbRegistryContract: any;
  userReputationContract: any;
  signer: any;
  provider: any;
  network: any;
  walletAddress: string;
  senderAddress: any; // native opnet Address object (for getContract sender param)
}

export function useEscrowClient(): OpnetEscrowClient | null {
  const { provider, signer, walletAddress, address, network } = useWalletConnect();

  return useMemo(() => {
    // getContract needs provider + network + sender (Address object, not string).
    // signer is only needed later for sendTransaction().
    if (!provider || !walletAddress || !network) return null;

    // `address` is the native opnet Address object from useWalletConnect.
    // getContract's `sender` parameter requires Address (has toHex(), etc.).
    // If address is null (wallet doesn't provide ML-DSA key), omit sender.
    const sender = address ?? undefined;

    const escrowContract = getContract(
      MOTOSETTLE_CONFIG.escrowCore,
      ESCROW_CORE_OPNET_ABI,
      provider,
      network,
      sender,
    );

    const routerContract = getContract(
      MOTOSETTLE_CONFIG.router,
      ROUTER_OPNET_ABI,
      provider,
      network,
      sender,
    );

    const feePolicyContract = getContract(
      MOTOSETTLE_CONFIG.feePolicy,
      FEE_POLICY_OPNET_ABI,
      provider,
      network,
      sender,
    );

    const arbRegistryContract = getContract(
      MOTOSETTLE_CONFIG.arbitratorRegistry,
      ARB_REGISTRY_OPNET_ABI,
      provider,
      network,
      sender,
    );

    const userReputationContract = getContract(
      MOTOSETTLE_CONFIG.userReputation,
      USER_REPUTATION_OPNET_ABI,
      provider,
      network,
      sender,
    );

    return {
      escrowContract,
      routerContract,
      feePolicyContract,
      arbRegistryContract,
      userReputationContract,
      signer: signer ?? null,
      provider,
      network,
      walletAddress,
      senderAddress: sender,
    };
  }, [provider, signer, walletAddress, address, network]);
}
